import React, { useState, useRef, useMemo, useCallback } from 'react';
import { devLog, devWarn, devError } from '../../../../utils/devLogger';
import GenericListUI, { FetchParams, FetchResult } from '../../../../components/CustomList/GenericListUI';
import { ColumnDefinition } from '../../../../components/CustomList/GenericDataTable';
import { 
  getLotList,
  createLot,
  updateLot, 
  deleteLot,
  updateLotRecommendStatus
} from '../../../../lib/api/admin/adminApi';
import { LotData } from '../../../../lib/api/admin/adminApi.types';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import LotFormPopup, { LotFormData } from './LotFormPopup';
import Switch from '../../../../components/Switch/Switch';

// 무료 환율 API를 통한 매도환율 조회 함수
const getExchangeRateForSale = async (): Promise<number> => {
  try {
    // 여러 무료 API를 순차적으로 시도
    const apis = [
      // 1. ExchangeRate-API (가장 안정적)
      {
        name: 'ExchangeRate-API',
        url: 'https://api.exchangerate-api.com/v4/latest/USD',
        parseRate: (data: any) => data.rates.KRW
      },
      // 2. Fawaz Ahmed의 Currency API (GitHub 기반)
      {
        name: 'Currency-API',
        url: 'https://cdn.jsdelivr.net/gh/fawazahmed0/currency-api@1/latest/currencies/usd/krw.json',
        parseRate: (data: any) => data.krw
      },
      // 3. Open Exchange Rates (제한적 무료)
      {
        name: 'CurrencyAPI',
        url: 'https://api.currencyapi.com/v3/latest?apikey=cur_live_free&currencies=KRW&base_currency=USD',
        parseRate: (data: any) => data.data?.KRW?.value
      }
    ];

    for (const api of apis) {
      try {
        devLog(`${api.name} API 시도 중...`);
        const response = await fetch(api.url);
        
        if (!response.ok) {
          devWarn(`${api.name} API 응답 오류:`, response.status);
          continue;
        }
        
        const data = await response.json();
        const baseRate = api.parseRate(data);
        
        if (baseRate && typeof baseRate === 'number' && baseRate > 0) {
          // 매도환율 계산 (은행 마진 1.5% 적용)
          const sellRate = Math.round(baseRate * 1.015 * 100) / 100;
          devLog(`${api.name} 매도환율:`, sellRate);
          return sellRate;
        }
      } catch (apiError) {
        devWarn(`${api.name} API 실패:`, apiError);
        continue;
      }
    }
    
    // 모든 API 실패 시 기본값 사용
    throw new Error('모든 환율 API 호출 실패');
    
  } catch (error) {
    devError('환율 조회 실패:', error);
    
    // 기본값 반환 (2024년 기준 대략적인 USD/KRW 매도환율)
    const defaultRate = 1350;
    devWarn(`환율 조회 실패, 기본값 사용: ${defaultRate}`);
    return defaultRate;
  }
};

// 이미지 URL 생성 함수 (LotFormPopup의 getImageSrc와 동일한 로직)
const getImageSrc = (imageUrl?: string | null) => {
  if (!imageUrl || imageUrl.trim() === "") {
    return "";
  }
  
  // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
  if (imageUrl.startsWith("http") || imageUrl.startsWith("blob:")) {
    return imageUrl;
  }
  
  // 절대 경로인 경우 (/로 시작)
  if (imageUrl.startsWith("/")) {
    return `${process.env.REACT_APP_API_HOST}${imageUrl}`;
  }
  
  // 단순 파일명인 경우 (서버에서 받은 파일명)
  return `${process.env.REACT_APP_API_HOST}/${imageUrl}`;
};

// UI에서 사용할 확장된 LOT 데이터 타입
interface UILotData extends LotData {
  // 기존 UI 호환성을 위한 필드들은 이미 LotData 타입에 포함됨
  exchangeRate?: number; // 환율 정보 추가
  auction?: {
    preBidStartAt?: string;
    auctionStartAt?: string;
    [key: string]: any; // 기타 경매 정보들
  };
}

const LotManagementPage: React.FC = () => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const listRef = useRef<{ refetch: () => void }>(null);
  const [existingRecommendNumbers, setExistingRecommendNumbers] = useState<number[]>([]);

  // 팝업 상태 관리
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentLotIndex, setCurrentLotIndex] = useState<number>(0); // 수정/삭제용 LOT index
  const [currentLotData, setCurrentLotData] = useState<LotFormData>({
    auctioneerName: '',
    auctioneerId: '',
    majorCategory: '',
    minorCategory: '',
    artworkName: '',
    startPriceKRW: null,
    thumbnailImages: [],
    artworkDetail: '',
    disabled: false,
  });
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof LotFormData, string>>>({});
  
  // 경매 정보 상태 추가
  const [currentAuctionData, setCurrentAuctionData] = useState<{
    preBidStartAt?: string;
    auctionStartAt?: string;
  } | null>(null);

  // 추천여부 토글 핸들러
  const handleRecommendToggle = useCallback(async (lotItem: UILotData, isRecommended: boolean) => {
    try {
      let recommendNo: number | null = null;
      
      if (isRecommended) {
        // 추천을 켜려고 할 때 3개 제한 확인
        if (existingRecommendNumbers.length >= 3) {
          toast.warning('최대 3개까지만 추천할 수 있습니다.');
          return;
        }
        
        // 비어있는 추천 번호 찾기 (1, 2, 3 중)
        for (let i = 1; i <= 3; i++) {
          if (!existingRecommendNumbers.includes(i)) {
            recommendNo = i;
            break;
          }
        }
      } else {
        recommendNo = null;
      }
      
      const response = await updateLotRecommendStatus(lotItem.index || 0, isRecommended, recommendNo);
      
      if (response?.data?.statusCode === 200 && response?.data?.message === "success") {
        toast.success(`추천여부가 ${isRecommended ? '활성화' : '비활성화'}되었습니다.`);
        setRefreshTrigger(prev => prev + 1);
      } else {
        const errorMessage = response?.data?.error?.customMessage || 
                            response?.data?.message || 
                            '추천여부 업데이트 중 오류가 발생했습니다.';
        toast.error(`추천여부 업데이트에 실패했습니다. (${errorMessage})`);
      }
    } catch (error: any) {
      devError('추천여부 업데이트 실패:', error);
      
      const errorMessage = error?.data?.error?.customMessage || 
                          error?.data?.message ||
                          error?.response?.data?.error?.customMessage || 
                          error?.response?.data?.message || 
                          error?.message || 
                          '네트워크 오류가 발생했습니다.';
      
      toast.error(`추천여부 업데이트에 실패했습니다. (${errorMessage})`);
    }
  }, [existingRecommendNumbers]);

  // 작품상세 핸들러
  const handleArtworkDetail = useCallback((lot: UILotData) => {
    try {
      // 테이블에서 받아온 auction 정보 사용
      let auctionData = null;
      if (lot.auction) {
        auctionData = {
          preBidStartAt: lot.auction.preBidStartAt,
          auctionStartAt: lot.auction.auctionStartAt,
        };
      }
      
      // categoryCode는 대분류 코드, subCategory 객체에서 소분류 코드를 가져옴
      const majorCategory = lot.categoryCode || '';
      const minorCategory = lot.subCategory?.code || ''; // subCategory 객체의 code 필드 사용
      
      // LOT 데이터를 팝업 폼 데이터로 변환
      setCurrentLotData({
        auctioneerName: lot.auctioneerName || '',
        auctioneerId: lot.auctioneerIndex?.toString() || '',
        majorCategory: majorCategory,
        minorCategory: minorCategory,
        artworkName: lot.name || '',
        startPriceKRW: lot.startPrice || null,
        thumbnailImages: lot.images || [],
        artworkDetail: lot.description || '',
        disabled: lot.disabled || false, // 서버에서 받은 disabled 값 사용
      });
      
      // 경매 정보 설정
      setCurrentAuctionData(auctionData);
      
      // LOT의 실제 index를 별도로 저장
      setCurrentLotIndex(lot.index || 0);
      setIsEditMode(true);
      setIsPopupOpen(true);
    } catch (error) {
      devError('LOT 상세 정보 설정 실패:', error);
      toast.error('LOT 정보를 불러오는 중 오류가 발생했습니다.');
    }
  }, []);

  // 가격 포맷팅 함수
  const formatPrice = useCallback((price: number, currency: 'KRW' | 'USD') => {
    if (currency === 'KRW') {
      return new Intl.NumberFormat('ko-KR', {
        style: 'currency',
        currency: 'KRW',
        minimumFractionDigits: 0,
      }).format(price);
    } else {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      }).format(price);
    }
  }, []);

  // 컬럼 정의
  const columns: ColumnDefinition<UILotData>[] = useMemo(() => [
    {
      header: 'No',
      accessor: 'no',
      formatter: (value: any) => value?.toString() || '',
    },
    {
      header: '등록일시',
      accessor: 'createAt',
      formatter: (value: string | number | Date | undefined) => {
        if (!value) return '';
        return dayjs(value).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      header: '경매사',
      accessor: 'auctioneerName',
      formatter: (value: any) => value || '',
    },
    {
      header: 'LOT ID',
      accessor: 'code',
      formatter: (value: any) => value || '',
    },
    {
      header: '추천여부',
      accessor: 'recommendNo',
      formatter: (value: any, item: UILotData) => {
        // recommendNo가 숫자 값이 있으면 true, null이거나 ''이면 false
        const isRecommended = value !== null && value !== undefined && value !== '';
        
        // 추천을 켜려고 할 때만 3개 제한 확인 (이미 추천된 상태면 끌 수 있어야 함)
        // 컬럼 정의에서는 disable 체크를 제거하고 handleRecommendToggle에서 처리
        const isDisabled = false;
        
        return (
          <RecommendContainer
            onClick={(e: React.MouseEvent) => e.stopPropagation()} // 클릭 이벤트 전파 방지
          >
            <Switch
              checked={isRecommended}
              onChange={(checked) => handleRecommendToggle(item, checked)}
              size="small"
              disabled={isDisabled}
            />
            {isRecommended && (
              <RecommendBadge>
                #{value}
              </RecommendBadge>
            )}
          </RecommendContainer>
        );
      },
    },
    {
      header: '철회',
      accessor: 'disabled',
      formatter: (value: any) => {
        const isDisabled = value === true;
        if (!isDisabled) {
          return null; // 정상적인 LOT는 아무것도 표시하지 않음
        }
        return (
          <StatusContainer>
            <StatusBadge $isDisabled={isDisabled}>
              철회
            </StatusBadge>
          </StatusContainer>
        );
      },
      excelFormatter: (value: any) => {
        return value === true ? 'Y' : 'N';
      },
    },
    {
      header: '카테고리 코드',
      accessor: 'categoryCode',
      formatter: (value: any, item: UILotData) => {
        const majorCategory = value || '';
        const minorCategory = item.subCategory?.code || '';
        
        if (majorCategory && minorCategory) {
          return `${majorCategory}-${minorCategory}`;
        } else if (majorCategory) {
          return majorCategory;
        } else {
          return '';
        }
      },
      excelFormatter: (value: any, item: UILotData) => {
        const majorCategory = value || '';
        const minorCategory = item.subCategory?.code || '';
        
        if (majorCategory && minorCategory) {
          return `${majorCategory}-${minorCategory}`;
        } else if (majorCategory) {
          return majorCategory;
        } else {
          return '';
        }
      },
    },
    {
      header: '작품명',
      accessor: 'artworkName',
      formatter: (value: any) => value || '',
    },
    {
      header: '시작가 (KRW)',
      accessor: 'startPriceKRW',
      formatter: (value: any) => {
        if (value === undefined || value === null) return '-';
        return formatPrice(value, 'KRW');
      },
    },
    {
      header: '시작가 (USD)',
      accessor: 'startPriceUSD',
      formatter: (value: any, item: UILotData) => {
        const krwPrice = item.startPrice || item.startPriceKRW;
        const exchangeRate = item.exchangeRate;
        
        if (!krwPrice || !exchangeRate) return '-';
        
        const usdPrice = Math.round((krwPrice / exchangeRate) * 100) / 100;
        return formatPrice(usdPrice, 'USD');
      },
    },
    {
      header: '썸네일이미지',
      accessor: 'images',
      formatter: (value: string[] | null | undefined) => {
        if (!value || !Array.isArray(value) || value.length === 0) {
          return '이미지 없음';
        }
        
        return (
          <ImageContainer>
            {value.slice(0, 3).map((imageUrl, index) => {
              const imageSrc = getImageSrc(imageUrl);
              return (
                <img 
                  key={index}
                  src={imageSrc} 
                  alt={`작품 썸네일 ${index + 1}`} 
                  style={{ 
                    width: '30px', 
                    height: '30px', 
                    objectFit: 'cover', 
                    borderRadius: '4px',
                    marginRight: index < value.length - 1 && index < 2 ? '4px' : '0'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              );
            })}
            {value.length > 3 && (
              <span style={{ fontSize: '12px', color: '#666', marginLeft: '4px' }}>
                +{value.length - 3}
              </span>
            )}
          </ImageContainer>
        );
      },
      excelFormatter: (value: string[] | null | undefined) => {
        if (!value || !Array.isArray(value) || value.length === 0) {
          return '이미지 없음';
        }
        
        // 모든 이미지 URL을 도메인+경로 형태로 변환하여 콤마로 구분해서 반환
        const fullUrls = value.map(imageUrl => getImageSrc(imageUrl)).filter(url => url);
        return fullUrls.join(', ');
      },
    },
    {
      header: '작품상세',
      accessor: 'id',
      showColumn: false, // Excel에서 컬럼 제외
      formatter: (value: any, item: UILotData) => (
        <ActionButton onClick={() => handleArtworkDetail(item)}>
          작품상세
        </ActionButton>
      ),
    },
  ], [handleArtworkDetail, formatPrice, handleRecommendToggle]);

  // 실제 API 함수 - auctioneers 페이지와 동일한 구조
  const fetchLotData = useCallback(async (params: FetchParams): Promise<FetchResult<UILotData>> => {
    devLog('fetchLotData 호출됨:', params);
    
    try {
      const response = await getLotList({
        keyword: params.keyword,
        fromDate: params.fromDate,
        toDate: params.toDate,
      });

      devLog('서버 응답:', response);

      // 서버 응답 구조: { statusCode, message, data: [], metadata: { allCnt, totalCnt } }
      let data: UILotData[] = [];
      let totalCount = 0;
      let allCount = 0;

      if (response?.data) {
        // 서버 응답이 { data: [], metadata: {} } 형태인 경우
        if (response.data.data && Array.isArray(response.data.data)) {
          const lotList = response.data.data;
          const metadata = response.data.metadata;
          
          // existingRecommendNo 상태 업데이트 - 간단하게 처리
          if (metadata?.existingRecommendNo && Array.isArray(metadata.existingRecommendNo)) {
            setExistingRecommendNumbers(metadata.existingRecommendNo);
            devLog('현재 추천된 LOT 번호들:', metadata.existingRecommendNo);
          } else {
            setExistingRecommendNumbers([]);
          }
          
          // 데이터 변환
          data = lotList.map((item: any) => ({
            ...item,
            // UI 호환성을 위한 필드 매핑
            id: item.index || item.id,
            artworkName: item.name,
            startPriceKRW: item.startPrice,
            auctioneerName: item.auctioneer?.name || '',
            auctioneerIndex: item.auctioneer?.index,
            categoryCode: item.category?.code || '',
            subCategory: item.subCategory,
            artworkDetail: item.description,
            exchangeRate: item.exchangeRate || 1350,
            auction: item.auction, // 경매 정보 추가
          }));
          
          // metadata가 있는 경우
          if (response.data.metadata) {
            totalCount = response.data.metadata.totalCnt || data.length;
            allCount = response.data.metadata.allCnt || data.length;
          } else {
            totalCount = data.length;
            allCount = data.length;
          }
        }
        // 예전 형태로 직접 배열인 경우 (호환성)
        else if (Array.isArray(response.data)) {
          data = response.data;
          totalCount = data.length;
          allCount = data.length;
        }
        // 다른 형태인 경우 빈 배열로 초기화
        else {
          devWarn('예상치 못한 서버 응답 구조:', response.data);
          data = [];
          totalCount = 0;
          allCount = 0;
        }
      }
      
      const result: FetchResult<UILotData> = {
        data: data,
        totalItems: totalCount,
        allItems: allCount,
      };

      devLog('가공된 결과:', result);
      return result;
    } catch (error) {
      toast.error('LOT 리스트를 불러오는 중 오류가 발생했습니다.');
      return {
        data: [],
        totalItems: 0,
        allItems: 0,
      };
    }
  }, []);

  // 새 LOT 추가 핸들러
  const handleAddLot = useCallback(() => {
    setCurrentLotData({
      auctioneerName: '',
      auctioneerId: '',
      majorCategory: '',
      minorCategory: '',
      artworkName: '',
      startPriceKRW: null,
      thumbnailImages: [],
      artworkDetail: '',
      disabled: false,
    });
    setIsEditMode(false);
    setIsPopupOpen(true);
  }, []);

  // 팝업 닫기 핸들러
  const handleClosePopup = useCallback(() => {
    setIsPopupOpen(false);
    setFormErrors({});
    setCurrentAuctionData(null); // 경매 정보도 초기화
  }, []);

  // 폼 데이터 변경 핸들러
  const handleInputChange = useCallback((field: keyof LotFormData, value: string | number | string[] | boolean | null) => {
    setCurrentLotData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // 에러 메시지 클리어
    setFormErrors(prev => {
      if (prev[field]) {
        return {
          ...prev,
          [field]: undefined
        };
      }
      return prev;
    });
  }, []);

  // 이미지 업로드 핸들러
  const handleImageUpload = useCallback((files: File[]) => {
    devLog('이미지 업로드:', files);
    // TODO: 실제 이미지 업로드 API 호출
  }, []);

  // 저장 핸들러
  const handleSave = useCallback(async (processedFormData?: Partial<LotFormData>) => {
    // processedFormData가 있으면 현재 데이터에 병합
    const dataToSave = processedFormData ? { ...currentLotData, ...processedFormData } : currentLotData;
    
    // 폼 검증
    const errors: Partial<Record<keyof LotFormData, string>> = {};
    
    if (!dataToSave.auctioneerName.trim()) {
      errors.auctioneerName = '경매사를 선택해주세요.';
    }
    if (!dataToSave.majorCategory) {
      errors.majorCategory = '대분류를 선택해주세요.';
    }
    if (!dataToSave.minorCategory) {
      errors.minorCategory = '소분류를 선택해주세요.';
    }
    if (!dataToSave.artworkName.trim()) {
      errors.artworkName = '작품명을 입력해주세요.';
    }
    if (!dataToSave.startPriceKRW || dataToSave.startPriceKRW < 1000) {
      errors.startPriceKRW = '시작가는 최소 1,000원 이상이어야 합니다.';
    }
    
    // HTML 태그를 제거하고 순수 텍스트만 추출하여 검증
    const stripHtmlTags = (html: string) => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    };
    
    const artworkDetailText = stripHtmlTags(dataToSave.artworkDetail).trim();
    if (!artworkDetailText) {
      errors.artworkDetail = 'LOT 설명을 입력해주세요.';
    }
    
    if (dataToSave.thumbnailImages.length === 0) {
      errors.thumbnailImages = '썸네일 이미지를 등록해주세요.';
    }

    const hasBlobThumbnail = dataToSave.thumbnailImages.some(imageUrl => !!imageUrl && imageUrl.startsWith('blob:'));
    if (hasBlobThumbnail) {
      errors.thumbnailImages = '업로드에 실패한 이미지가 있습니다. 다시 업로드 후 저장해주세요.';
      toast.error('업로드에 실패한 이미지가 있어 저장할 수 없습니다. 다시 업로드 후 저장해주세요.');
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // 환율 조회 중임을 사용자에게 알림
    // toast.info('환율 정보를 조회하고 있습니다...');

    try {
      // 전날 매도환율 조회
      const exchangeRate = await getExchangeRateForSale();
      devLog('적용된 매도환율:', exchangeRate);
      
      // API 호출용 데이터 변환
      const apiData = {
        name: dataToSave.artworkName,
        description: dataToSave.artworkDetail, // 이미지 처리가 완료된 콘텐츠
        images: dataToSave.thumbnailImages,
        startPrice: dataToSave.startPriceKRW || 1000, // null인 경우 기본값 1000 사용
        auctioneerIndex: parseInt(dataToSave.auctioneerId) || 1, // 기본값 설정
        categoryCode: dataToSave.majorCategory, // 대분류만 categoryCode로 전송
        subCategoryCode: dataToSave.minorCategory, // 소분류는 string으로 전송
        exchangeRate: exchangeRate, // 전날 매도환율 추가
        disabled: dataToSave.disabled, // LOT 활성화/비활성화 상태
      };

      if (isEditMode) {
        // 수정 모드
        devLog('LOT 수정:', currentLotIndex, apiData);
        const response = await updateLot(currentLotIndex, apiData);
        
        // API 응답 구조 확인: statusCode가 200이고 message가 "success"일 때만 성공
        if (response?.data?.statusCode === 200 && response?.data?.message === "success") {
          toast.success(`LOT이 수정되었습니다. (환율: ${exchangeRate.toLocaleString()}원)`);
          handleClosePopup();
          setRefreshTrigger(prev => prev + 1);
        } else {
          // statusCode가 200이 아니거나 message가 "success"가 아닌 경우
          const errorMessage = response?.data?.error?.customMessage || 
                              response?.data?.message || 
                              '수정 처리 중 오류가 발생했습니다.';
          toast.error(`LOT 수정에 실패했습니다. (${errorMessage})`);
        }
      } else {
        // 생성 모드
        devLog('LOT 생성:', apiData);
        const response = await createLot(apiData);
        
        // API 응답 구조 확인: statusCode가 200이고 message가 "success"일 때만 성공
        if (response?.data?.statusCode === 200 && response?.data?.message === "success") {
          toast.success(`LOT이 등록되었습니다. (환율: ${exchangeRate.toLocaleString()}원)`);
          handleClosePopup();
          setRefreshTrigger(prev => prev + 1);
        } else {
          // statusCode가 200이 아니거나 message가 "success"가 아닌 경우
          const errorMessage = response?.data?.error?.customMessage || 
                              response?.data?.message || 
                              '등록 처리 중 오류가 발생했습니다.';
          toast.error(`LOT 등록에 실패했습니다. (${errorMessage})`);
        }
      }
      
    } catch (error: any) {
      devError('LOT 저장 실패:', error);
      
      // callApi에서 던진 에러 처리
      const errorMessage = error?.data?.error?.customMessage || 
                          error?.data?.message ||
                          error?.response?.data?.error?.customMessage || 
                          error?.response?.data?.message || 
                          error?.message || 
                          '네트워크 오류가 발생했습니다.';
      
      if (isEditMode) {
        toast.error(`LOT 수정에 실패했습니다. (${errorMessage})`);
      } else {
        toast.error(`LOT 등록에 실패했습니다. (${errorMessage})`);
      }
      // 실패 시에는 팝업을 닫지 않음
    }
  }, [currentLotData, isEditMode, currentLotIndex, handleClosePopup]);

  // 삭제 핸들러
  const handleDelete = useCallback(async () => {
    if (!isEditMode || !currentLotIndex) {
      devError('삭제할 LOT 정보가 없습니다.');
      return;
    }

    const confirmDelete = window.confirm('정말로 이 LOT을 삭제하시겠습니까?');
    if (!confirmDelete) return;

    try {
      devLog('LOT 삭제:', currentLotIndex);
      const response = await deleteLot(currentLotIndex);
      
      // API 응답 구조 확인: statusCode가 200이고 message가 "success"일 때만 성공
      if (response?.data?.statusCode === 200 && response?.data?.message === "success") {
        toast.success('LOT이 삭제되었습니다.');
        handleClosePopup();
        setRefreshTrigger(prev => prev + 1);
      } else {
        // statusCode가 200이 아니거나 message가 "success"가 아닌 경우
        const errorMessage = response?.data?.error?.customMessage || 
                            response?.data?.message || 
                            '삭제 처리 중 오류가 발생했습니다.';
        toast.error(`LOT 삭제에 실패했습니다. (${errorMessage})`);
      }
    } catch (error: any) {
      devError('LOT 삭제 실패:', error);
      
      // callApi에서 던진 에러 처리
      const errorMessage = error?.data?.error?.customMessage || 
                          error?.data?.message ||
                          error?.response?.data?.error?.customMessage || 
                          error?.response?.data?.message || 
                          error?.message || 
                          '네트워크 오류가 발생했습니다.';
      
      toast.error(`LOT 삭제에 실패했습니다. (${errorMessage})`);
    }
  }, [isEditMode, currentLotIndex, handleClosePopup]);

  // 행 클릭 핸들러
  const handleRowClick = useCallback((item: UILotData, index: number) => {
    devLog('LOT 선택:', item);
    handleArtworkDetail(item); // 행 클릭 시 상세 정보 팝업 열기
  }, [handleArtworkDetail]);

  return (
    <>
      <GenericListUI<UILotData>
        ref={listRef}
        title="LOT 관리"
        columns={columns}
        fetchData={fetchLotData}
        excelFileName="LotList"
        keyExtractor={(item, index) => {
          // 고유한 키 보장: 여러 필드를 조합하여 중복 방지
          const itemKey = item.index || item.id;
          const itemName = (item.artworkName || item.name || '').replace(/\s+/g, '');
          const timestamp = item.createAt ? new Date(item.createAt).getTime() : Date.now();
          
          // 복합 키로 고유성 보장
          return `lot-${itemKey}-${index}-${itemName.substring(0, 10)}-${timestamp}`;
        }}
        enableSearch={true}
        searchPlaceholder="LOT ID, 경매사, 카테고리, 작품명으로 검색"
        enableDateFilter={true}
        dateRangeOptions={['3개월', '6개월', '1년', '지정']}
        itemsPerPageOptions={[10, 20, 50, 100]}
        themeMode="light"
        onRowClick={handleRowClick}
        refreshTrigger={refreshTrigger}
        addButton={{
          label: "LOT 추가",
          onClick: handleAddLot
        }}
      />
      
      <LotFormPopup
        isOpen={isPopupOpen}
        isEditMode={isEditMode}
        formData={currentLotData}
        formErrors={formErrors}
        onClose={handleClosePopup}
        onSave={handleSave}
        onDelete={handleDelete}
        onInputChange={handleInputChange}
        onImageUpload={handleImageUpload}
        auctionData={currentAuctionData || undefined} // 경매 정보 전달
      />
    </>
  );
};

export default LotManagementPage;

// Styled Components
const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 2px;
`;

const ActionButton = styled.button`
  background-color: #28a745;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background-color: #218838;
  }
  
  &:active {
    background-color: #1e7e34;
  }
`;

const RecommendContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  height: 100%;
  padding: 4px 8px;
  cursor: default;
`;

const RecommendBadge = styled.span`
  font-size: 12px;
  color: #666;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: bold;
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 4px 8px;
`;

const StatusBadge = styled.span<{ $isDisabled: boolean }>`
  font-size: 12px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: #ff6b6b;
  color: white;
  text-align: center;
  min-width: 40px;
`;
